import type { Card, Game, GameStoreState } from "./types";

const validSuits = new Set<Card["suit"]>([
  "spades",
  "hearts",
  "diamonds",
  "clubs",
  "joker",
]);

const validSyncStatuses = new Set<NonNullable<Game["syncStatus"]>>([
  "synced",
  "syncing",
  "failed",
  "unsynced",
]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isInteger = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value);

const isCard = (value: unknown): value is Card => {
  if (!isRecord(value) || !isInteger(value.court)) return false;
  return validSuits.has(value.suit as Card["suit"]);
};

const isNumberArray = (value: unknown): value is number[] =>
  Array.isArray(value) && value.every(isInteger);

const isValidGame = (value: unknown): value is Game => {
  if (!isRecord(value)) return false;

  const {
    cards,
    courts,
    lastDrawn,
    perCourt,
    players,
    roundId,
    shortCourt,
    slug,
    syncStatus,
    updatedAt,
  } = value;

  if (slug !== null && typeof slug !== "string") return false;
  if (!isInteger(lastDrawn) || lastDrawn < -1) return false;
  if (typeof updatedAt !== "number" || !Number.isFinite(updatedAt))
    return false;
  if (cards !== undefined && (!Array.isArray(cards) || !cards.every(isCard))) {
    return false;
  }
  if (courts !== undefined && !isNumberArray(courts)) return false;
  if (players !== undefined && (!isInteger(players) || players < 0))
    return false;
  if (perCourt !== undefined && (!isInteger(perCourt) || perCourt <= 0)) {
    return false;
  }
  if (roundId !== undefined && typeof roundId !== "string") return false;
  if (shortCourt !== undefined && !isInteger(shortCourt)) return false;
  if (
    syncStatus !== undefined &&
    !validSyncStatuses.has(syncStatus as NonNullable<Game["syncStatus"]>)
  ) {
    return false;
  }

  const cardCount = Array.isArray(cards) ? cards.length : 0;
  if (lastDrawn >= cardCount || (cards === undefined && lastDrawn !== -1)) {
    return false;
  }
  return true;
};

export const createInitialGame = (now: number = Date.now()): Game => ({
  slug: null,
  cards: undefined,
  courts: undefined,
  players: undefined,
  lastDrawn: -1,
  perCourt: 4,
  shortCourt: undefined,
  updatedAt: now,
});

const copyGame = (game: Game): Game => ({
  slug: game.slug,
  cards: game.cards?.map((card) => ({ ...card })),
  courts: game.courts ? [...game.courts] : undefined,
  players: game.players,
  lastDrawn: game.lastDrawn,
  perCourt: game.perCourt,
  roundId: game.roundId,
  shortCourt:
    game.shortCourt !== undefined && game.courts?.includes(game.shortCourt)
      ? game.shortCourt
      : undefined,
  updatedAt: game.updatedAt,
  // A change can't still be on its way after a reload, and otherwise it
  // blocks every change; a shared game catches up from the server.
  syncStatus: game.syncStatus === "syncing" ? "unsynced" : game.syncStatus,
});

const cardsMatch = (left: unknown, right: Card | undefined) =>
  right === undefined
    ? left === undefined
    : isCard(left) && left.court === right.court && left.suit === right.suit;

export interface NormalizedGameState {
  state: GameStoreState;
  recovered: boolean;
  gameWasReset: boolean;
}

export const normalizePersistedGameState = (
  persistedState: unknown,
  now: number = Date.now(),
): NormalizedGameState => {
  const defaultState: GameStoreState = {
    game: createInitialGame(now),
    drawnIndex: -1,
    currentCard: undefined,
    localDrawnCards: [],
  };

  if (!isRecord(persistedState)) {
    const recovered = persistedState !== undefined;
    return { state: defaultState, recovered, gameWasReset: recovered };
  }

  if (!isValidGame(persistedState.game)) {
    const recovered = persistedState.game !== undefined;
    return { state: defaultState, recovered, gameWasReset: recovered };
  }

  const game = copyGame(persistedState.game);
  const rawLocalDrawnCards = persistedState.localDrawnCards;
  const localDrawnCardsAreValid =
    isNumberArray(rawLocalDrawnCards) &&
    new Set(rawLocalDrawnCards).size === rawLocalDrawnCards.length &&
    rawLocalDrawnCards.every(
      (index) => index >= 0 && index <= game.lastDrawn && game.cards?.[index],
    );
  const localDrawnCards = localDrawnCardsAreValid
    ? [...rawLocalDrawnCards]
    : [];

  const rawDrawnIndex = persistedState.drawnIndex;
  const drawnIndexIsValid =
    isInteger(rawDrawnIndex) &&
    ((localDrawnCards.length === 0 && rawDrawnIndex === -1) ||
      (localDrawnCards.length > 0 &&
        rawDrawnIndex >= 0 &&
        rawDrawnIndex < localDrawnCards.length));
  const drawnIndex = drawnIndexIsValid ? rawDrawnIndex : -1;
  const deckIndex = drawnIndex >= 0 ? localDrawnCards[drawnIndex] : undefined;
  const currentCard =
    deckIndex === undefined ? undefined : game.cards?.[deckIndex];

  const recovered =
    game.shortCourt !== persistedState.game.shortCourt ||
    !localDrawnCardsAreValid ||
    !drawnIndexIsValid ||
    !cardsMatch(persistedState.currentCard, currentCard);

  return {
    state: { game, drawnIndex, currentCard, localDrawnCards },
    recovered,
    gameWasReset: false,
  };
};
